/**
 * REST data provider for the /org-units API.
 *
 * Org unit IDs are canonical paths. They are encoded only at the URL boundary:
 *   path/id "/aetc/2af" -> URL segment "%2Faetc%2F2af"
 *
 * The provider shapes records to always include `id` for react-admin.
 */

import type { DataProvider } from 'react-admin';
import { getListSort, getListFilter } from './list-utils';

import { apiFetch } from './api-rest-provider';

const encodeId = (id: string): string => encodeURIComponent(id);

const orgUnitUrl = (id?: string): string =>
  id ? `org-units/${encodeId(id)}` : 'org-units';

/** Normalise a raw API record so react-admin gets `id` and `path` fields. */
const normalise = (value: unknown): Record<string, unknown> & { id: string; path: string } => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid org-unit API record');
  }
  const record = value as Record<string, unknown>;
  const path = typeof record.path === 'string'
    ? record.path
    : (record.sk ? String(record.sk).slice('PATH#'.length) : undefined);
  if (!path) throw new Error('Org-unit API record is missing its path identifier');
  const id: string = path;
  return { ...record, path, id };
};

type OrgUnitProvider = Pick<
  DataProvider,
  | 'getList'
  | 'getOne'
  | 'create'
  | 'update'
  | 'delete'
  | 'getMany'
  | 'getManyReference'
  | 'updateMany'
  | 'deleteMany'
>;

export const orgUnitsProvider = ({
  getList: async (_resource, params) => {
    const { sort = { field: 'path', order: 'ASC' }, filter = {}, pagination = { page: 1, perPage: 25 } } = params || {};
    const { page = 1, perPage = 25 } = pagination;

    const raw = await apiFetch(orgUnitUrl());
    // API may return an array directly or { items: [] }
    const allItems = (Array.isArray(raw) ? raw : raw?.items ?? []).map(normalise);

    const filtered = allItems.filter(getListFilter(filter));
    const sorted = filtered.sort(getListSort(sort));
    const paged = sorted.slice((page - 1) * perPage, page * perPage);

    return { data: paged, total: filtered.length };
  },

  getOne: async (_resource, params) => {
    const raw = await apiFetch(orgUnitUrl(String(params.id)));
    return { data: normalise(raw) };
  },

  getMany: async (_resource, params) => {
    const items = await Promise.all(
      params.ids.map(async (id) => {
        const raw = await apiFetch(orgUnitUrl(String(id)));
        return normalise(raw);
      }),
    );
    return { data: items };
  },

  getManyReference: async (_resource, params) => {
    const { page = 1, perPage = 25 } = params.pagination || {};
    const raw = await apiFetch(orgUnitUrl());
    const allItems = (Array.isArray(raw) ? raw : raw?.items ?? []).map(normalise);
    const filter = { ...params.filter, [params.target]: params.id };
    const filtered = allItems.filter(getListFilter(filter));
    const paged = filtered.sort(getListSort(params.sort)).slice((page - 1) * perPage, page * perPage);
    return { data: paged, total: filtered.length };
  },

  create: async (_resource, params) => {
    const raw = await apiFetch(orgUnitUrl(), {
      method: 'POST',
      body: JSON.stringify(params.data),
    });
    return { data: normalise(raw) };
  },

  update: async (_resource, params) => {
    const raw = await apiFetch(orgUnitUrl(String(params.id)), {
      method: 'PUT',
      body: JSON.stringify(params.data),
    });
    return { data: normalise(raw ?? { ...params.data, path: params.id }) };
  },

  delete: async (_resource, params) => {
    await apiFetch(orgUnitUrl(String(params.id)), { method: 'DELETE' });
    return { data: { id: params.id } };
  },

  updateMany: async (_resource, params) => {
    await Promise.all(
      params.ids.map((id) =>
        apiFetch(orgUnitUrl(String(id)), {
          method: 'PUT',
          body: JSON.stringify(params.data),
        }),
      ),
    );
    return { data: params.ids };
  },

  deleteMany: async (_resource, params) => {
    await Promise.all(
      params.ids.map((id) => apiFetch(orgUnitUrl(String(id)), { method: 'DELETE' })),
    );
    return { data: params.ids };
  },
}) as OrgUnitProvider;
